import type {
  TargetareCampanie,
  TargetareCanalCampanie,
  TargetareStareCampanie,
  TargetareStareTinta,
  TargetareTinta,
} from "@/types/targetare";

export const ETICHETE_STARE_TINTA: Record<TargetareStareTinta, string> = {
  de_contactat: "De contactat",
  contactat: "Contactat",
  oferta_trimisa: "Ofertă trimisă",
  in_discutie: "În discuție",
  castigat: "Câștigat",
  pierdut: "Pierdut",
  inactiv: "Inactiv",
};

export const STARI_TINTA = Object.keys(
  ETICHETE_STARE_TINTA
) as TargetareStareTinta[];

export const CLASE_STARE_TINTA: Record<TargetareStareTinta, string> = {
  de_contactat: "bg-muted text-muted-foreground",
  contactat: "bg-ds-blue-100 text-ds-blue-700",
  oferta_trimisa: "bg-ds-blue-200 text-ds-blue-800",
  in_discutie: "bg-yellow-100 text-yellow-800",
  castigat: "bg-ds-green-100 text-ds-green-700",
  pierdut: "bg-ds-red-100 text-ds-red-700",
  inactiv: "bg-muted text-muted-foreground line-through",
};

export const ETICHETE_CANAL: Record<TargetareCanalCampanie, string> = {
  email: "Email (Mailchimp)",
  google_ads: "Google Ads",
  linkedin: "LinkedIn",
  telefon: "Telefon",
  eveniment: "Eveniment",
  altul: "Altul",
};

export const ETICHETE_STARE_CAMPANIE: Record<TargetareStareCampanie, string> = {
  planificata: "Planificată",
  activa: "Activă",
  incheiata: "Încheiată",
};

export const ETICHETE_PRIORITATE: Record<number, string> = {
  1: "Ridicată",
  2: "Normală",
  3: "Scăzută",
};

const celulaCsv = (s: string): string =>
  /[",\n\r;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;

const linieCsv = (valori: string[]): string =>
  valori.map((v) => celulaCsv(v ?? "")).join(",");

export function numeCampanie(
  campanii: TargetareCampanie[],
  id: number | null
): string {
  if (id === null) return "";
  return campanii.find((c) => c.id === id)?.nume ?? `#${id}`;
}

function persoanaContact(t: TargetareTinta): { prenume: string; nume: string } {
  const sursa = t.director_it ?? t.conducere?.split(";")[0] ?? "";
  const nume = sursa.replace(/\(.*$/, "").trim();
  if (!nume) return { prenume: "", nume: "" };
  const parti = nume.split(/\s+/);
  if (parti.length === 1) return { prenume: "", nume: parti[0] };
  return {
    prenume: parti[parti.length - 1],
    nume: parti.slice(0, -1).join(" "),
  };
}

export function csvMailchimp(
  tinte: TargetareTinta[],
  campanii: TargetareCampanie[]
): string {
  const antet = [
    "Email Address",
    "First Name",
    "Last Name",
    "Company",
    "CUI",
    "Sector",
    "Judet",
    "Nivel risc",
    "Tags",
  ];
  const linii = tinte
    .filter((t) => t.email_principal)
    .map((t) => {
      const p = persoanaContact(t);
      const tags = [
        t.sector ?? "",
        t.clasificare_estimata ?? "",
        t.nivel_risc ?? "",
        numeCampanie(campanii, t.campanie_id),
      ]
        .filter(Boolean)
        .join(", ");
      return linieCsv([
        t.email_principal ?? "",
        p.prenume,
        p.nume,
        t.denumire ?? "",
        t.cui,
        t.sector ?? "",
        t.judet ?? "",
        t.nivel_risc ?? "",
        tags,
      ]);
    });
  return "﻿" + [linieCsv(antet), ...linii].join("\r\n");
}

export function csvGoogleAds(tinte: TargetareTinta[]): string {
  const antet = ["Email", "Phone", "First Name", "Last Name", "Country", "Zip"];
  const linii = tinte
    .filter((t) => t.email_principal || t.telefon_principal)
    .map((t) => {
      const p = persoanaContact(t);
      return linieCsv([
        t.email_principal ?? "",
        t.telefon_principal ?? "",
        p.prenume,
        p.nume,
        "RO",
        t.cod_postal ?? "",
      ]);
    });
  return [linieCsv(antet), ...linii].join("\r\n");
}

export function cuiuriDinText(text: string): string[] {
  const gasite = text.match(/\d{2,10}/g) ?? [];
  return [...new Set(gasite)];
}
